import { useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DAYS_OF_WEEK } from "@shared/types/foyer";
import type {
  OfferAvailability,
  OfferOverridePatch,
  ResolvedRoomOffer,
  RoomOffer,
} from "@shared/types/roomOffer";
import { offerOverrideProblems } from "@shared/roomOfferLayers";
import { readLocalized } from "@shared/i18n";
import AssetField from "../AssetField";
import { PermissionGate } from "../../auth/access";
import { tokens } from "../../theme";
import { ItemFormSurface } from "../EditDrawer";
import { useFormat, type Formatters } from "../../i18n/format";
import type { Translate } from "../../i18n/translate";
import {
  offerProblemMessage,
  shortDayLabel,
  type OfferProblem,
} from "./offerCopy";

/**
 * Re-decide one inherited offer for THIS hotel (`POS-14`).
 *
 * ## Why this is not the offer editor with the fields greyed out
 *
 * Because the two dialogs answer different questions. `OfferDialog` asks "what
 * is this offer?"; this one asks "what is different about it HERE?" — and the
 * difference is the whole record. Every control below is a three-state one:
 *
 * | state | what it means | what is stored |
 * |---|---|---|
 * | **Inherit** | whatever the chain says, now and after the chain edits it | the key is ABSENT |
 * | **Free / no picture** | this property decided there is none | the key is `null` |
 * | a value | this property decided this one | the value |
 *
 * Absent and null being different is not pedantry: collapsing them would make
 * "free at this hotel" impossible to express, and the operator would express it
 * by COPYING the offer instead — which silently stops tracking the chain
 * original, and nobody notices until the chain edits and one hotel does not
 * change. That copy is the failure this dialog exists to prevent, so the
 * inherit state is the default on every field and it is spelled out on screen.
 *
 * Title, description and the pick list are deliberately absent. A property that
 * could re-write those would be authoring its own offer with extra steps, and
 * the chain would have no idea what its campaign says in that building.
 */

export interface OfferOverrideDialogProps {
  /** The row as this property currently sees it — patch already applied. */
  offer: ResolvedRoomOffer;
  /**
   * What the chain (or Nenda) actually authored — the UNPATCHED row.
   *
   * Passed separately so every control can say what it is inheriting. A dialog
   * that only knew the patched row could not tell an operator what they would
   * go back to, which is the one thing they are deciding about.
   */
  original: RoomOffer;
  saving?: boolean;
  error?: string | null;
  serverProblems?: OfferProblem[];
  onClose: () => void;
  /**
   * The PAGE saves. Resolve `true` on success and the surface slides out
   * (then calls `onClose`); `false` keeps it open to show the refusal.
   */
  onSave: (patch: OfferOverridePatch) => Promise<boolean>;
  /** Offered only when a delta already exists. Same contract as `onSave`. */
  onRevert?: () => Promise<boolean>;
}

type PriceMode = "inherit" | "free" | "custom";

function priceOf(offer: RoomOffer) {
  return "price" in offer.action ? offer.action.price : undefined;
}

/** What the chain charges, as one line for the hint under the control. */
function inheritedPriceLabel(t: Translate, original: RoomOffer): string {
  const price = priceOf(original);
  if (!("price" in original.action)) {
    return t("offers:offerOverrideDialog.kindHasNoPrice");
  }
  return price
    ? `${price.amount} ${price.currency}`
    : t("offers:offerOverrideDialog.free");
}

function windowLabel(
  t: Translate,
  fmt: Formatters,
  a: OfferAvailability
): string {
  const parts: string[] = [];
  if (a.startDate || a.endDate) {
    parts.push(
      t("offers:offerOverrideDialog.dateRange", {
        from: a.startDate ?? "…",
        to: a.endDate ?? "…",
      })
    );
  }
  if (a.startTime || a.endTime) {
    parts.push(`${a.startTime ?? "00:00"}–${a.endTime ?? "00:00"}`);
  }
  if (a.days && a.days.length > 0) {
    parts.push(a.days.map((d) => shortDayLabel(fmt, d)).join(" "));
  }
  return parts.length > 0
    ? parts.join(" · ")
    : t("offers:offerOverrideDialog.alwaysOn");
}

export default function OfferOverrideDialog({
  offer,
  original,
  saving,
  error,
  serverProblems,
  onClose,
  onSave,
  onRevert,
}: OfferOverrideDialogProps) {
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const fields = offer.overriddenFields;
  const kindHasPrice = original.action.kind !== "promotion";

  const [priceMode, setPriceMode] = useState<PriceMode>(() => {
    if (!fields.includes("price")) return "inherit";
    return priceOf(offer) ? "custom" : "free";
  });
  const [amount, setAmount] = useState<string>(() =>
    priceOf(offer) ? String(priceOf(offer)?.amount) : ""
  );
  const [currency, setCurrency] = useState<string>(
    () => priceOf(offer)?.currency || priceOf(original)?.currency || "kr"
  );

  const [ownWindow, setOwnWindow] = useState(fields.includes("availability"));
  const [availability, setAvailability] = useState<OfferAvailability>(
    () => offer.availability ?? {}
  );

  const [ownImage, setOwnImage] = useState(fields.includes("imageUrl"));
  const [imageUrl, setImageUrl] = useState(offer.imageUrl ?? "");

  const patch = useMemo<OfferOverridePatch>(() => {
    const next: OfferOverridePatch = {};
    if (kindHasPrice && priceMode !== "inherit") {
      next.price =
        priceMode === "free"
          ? null
          : {
              // A BLANK amount is NaN, never 0. `Number("")` is zero, and a
              // zero saved here would put a "0 kr" line into the revenue
              // series — §2's rule, and the same one `OfferDialog` states.
              // Free is its own choice above; an empty box is an unfinished
              // one, and NaN is what makes `moneyProblems()` say so and hold
              // the Save button.
              amount: amount.trim() === "" ? Number.NaN : Number(amount.trim()),
              currency: currency.trim(),
            };
    }
    if (ownWindow) next.availability = availability;
    if (ownImage) next.imageUrl = imageUrl.trim() === "" ? null : imageUrl;
    return next;
  }, [
    kindHasPrice,
    priceMode,
    amount,
    currency,
    ownWindow,
    availability,
    ownImage,
    imageUrl,
  ]);

  // Checked here as well as on the server, with the SAME function, so a bad
  // amount is caught before a round trip rather than only after one.
  const problems = useMemo(
    () => [
      ...offerOverrideProblems(patch).map((code) =>
        offerProblemMessage(t, code)
      ),
      ...(serverProblems ?? []),
    ],
    [patch, serverProblems, t]
  );
  const forField = (field: OfferProblem["field"]) =>
    problems.find((p) => p.field === field)?.message;

  const setWindow = (part: Partial<OfferAvailability>) =>
    setAvailability((prev) => ({ ...prev, ...part }));
  const text = (v: string) => (v.trim() === "" ? undefined : v);

  /*
   * Creating the first override is a modal; re-opening one this location
   * already holds (the row menu reads "Edit this location's override" on the
   * same test) slides in from the right.
   */
  const editing = offer.overriddenFields.length > 0 || offer.suppressed;

  return (
    <ItemFormSurface
      mode={editing ? "edit" : "create"}
      onClose={onClose}
      maxWidth="sm"
      busy={saving}
      title={
        editing
          ? t("offerOverrideDialog.editTitle")
          : t("offerOverrideDialog.newTitle")
      }
      subtitle={t("offerOverrideDialog.subtitle", {
        title:
          readLocalized(original.title, "sv") ||
          t("offerOverrideDialog.untitled"),
        origin:
          offer.origin === "company"
            ? t("offerOverrideDialog.fromChain")
            : t("offerOverrideDialog.fromNenda"),
      })}
      actions={(dismiss) => (
        <>
          {onRevert && (
            <PermissionGate
              resource="offers"
              level="edit"
              deniedProps={{ disabled: true }}
            >
              <Button
                color="inherit"
                onClick={async () => {
                  if (await onRevert()) dismiss();
                }}
                disabled={saving}
                sx={{ mr: "auto" }}
                data-testid="override-revert"
              >
                {t("offerOverrideDialog.stopOverriding")}
              </Button>
            </PermissionGate>
          )}
          <Button color="inherit" onClick={() => dismiss()} disabled={saving}>
            {t("common:buttons.cancel")}
          </Button>
          <PermissionGate
            resource="offers"
            level="edit"
            deniedProps={{ disabled: true }}
          >
            <Button
              variant="contained"
              onClick={async () => {
                if (await onSave(patch)) dismiss();
              }}
              disabled={saving || problems.length > 0}
              data-testid="override-save"
            >
              {t("offerOverrideDialog.saveForLocation")}
            </Button>
          </PermissionGate>
        </>
      )}
    >
      <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
        <Trans
          t={t}
          i18nKey="offerOverrideDialog.inheritNote"
          components={{ strong: <strong /> }}
        />
      </Alert>

      <Stack spacing={2.5}>
        {kindHasPrice && (
          <Box>
            <Stack direction="row" spacing={1}>
              <TextField
                select
                size="small"
                label={t("offerOverrideDialog.priceHere")}
                value={priceMode}
                onChange={(e) => setPriceMode(e.target.value as PriceMode)}
                sx={{ width: 200, flex: "none" }}
                inputProps={{ "data-testid": "override-price-mode" }}
              >
                <MenuItem value="inherit">
                  {t("offerOverrideDialog.inheritPrice", {
                    price: inheritedPriceLabel(t, original),
                  })}
                </MenuItem>
                {/* Free is its own choice, never "0". A zero would put a
                      0 kr line into the revenue series — §2. */}
                <MenuItem value="free">
                  {t("offerOverrideDialog.freeHere")}
                </MenuItem>
                <MenuItem value="custom">
                  {t("offerOverrideDialog.differentPrice")}
                </MenuItem>
              </TextField>
              {priceMode === "custom" && (
                <>
                  <TextField
                    size="small"
                    label={t("common:labels.price")}
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {currency}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, "data-testid": "override-price" }}
                    error={Boolean(forField("price"))}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    label={t("common:labels.currency")}
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    inputProps={{ maxLength: 8 }}
                    sx={{ width: 96, flex: "none" }}
                  />
                </>
              )}
            </Stack>
            {forField("price") && (
              <Typography
                variant="meta"
                role="alert"
                sx={{ mt: 0.75, color: tokens.error }}
              >
                {forField("price")}
              </Typography>
            )}
          </Box>
        )}

        <Box>
          <TextField
            select
            size="small"
            label={t("offerOverrideDialog.whenShowsHere")}
            value={ownWindow ? "own" : "inherit"}
            onChange={(e) => setOwnWindow(e.target.value === "own")}
            fullWidth
            inputProps={{ "data-testid": "override-window-mode" }}
            helperText={
              ownWindow
                ? t("offerOverrideDialog.ownWindowHelp")
                : t("offerOverrideDialog.inheritedWindow", {
                    window: windowLabel(t, fmt, original.availability ?? {}),
                  })
            }
          >
            <MenuItem value="inherit">
              {t("offerOverrideDialog.inheritDates")}
            </MenuItem>
            <MenuItem value="own">{t("offerOverrideDialog.ownDates")}</MenuItem>
          </TextField>
          {ownWindow && (
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label={t("common:labels.from")}
                  type="date"
                  value={availability.startDate ?? ""}
                  onChange={(e) =>
                    setWindow({ startDate: text(e.target.value) })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                  inputProps={{ "data-testid": "override-window-start-date" }}
                />
                <TextField
                  size="small"
                  label={t("offerDialog.until")}
                  type="date"
                  value={availability.endDate ?? ""}
                  onChange={(e) => setWindow({ endDate: text(e.target.value) })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                  inputProps={{ "data-testid": "override-window-end-date" }}
                />
              </Stack>
              <Stack direction="row" spacing={1}>
                <TextField
                  size="small"
                  label={t("offerDialog.showsFrom")}
                  type="time"
                  value={availability.startTime ?? ""}
                  onChange={(e) =>
                    setWindow({ startTime: text(e.target.value) })
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label={t("offerDialog.showsUntil")}
                  type="time"
                  value={availability.endTime ?? ""}
                  onChange={(e) => setWindow({ endTime: text(e.target.value) })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ flex: 1 }}
                />
              </Stack>
              <Box>
                <Typography
                  variant="meta"
                  sx={{ color: tokens.inkMuted, mb: 0.5 }}
                >
                  {t("offerOverrideDialog.daysNoneMeansEvery")}
                </Typography>
                <Stack direction="row" gap={0.75} flexWrap="wrap">
                  {DAYS_OF_WEEK.map((day) => {
                    const on = (availability.days ?? []).includes(day);
                    return (
                      <Button
                        key={day}
                        size="small"
                        variant={on ? "contained" : "outlined"}
                        onClick={() => {
                          const days = new Set(availability.days ?? []);
                          if (on) days.delete(day);
                          else days.add(day);
                          const next = DAYS_OF_WEEK.filter((d) => days.has(d));
                          setWindow({
                            days: next.length > 0 ? next : undefined,
                          });
                        }}
                        sx={{ minWidth: 46, px: 0 }}
                      >
                        {shortDayLabel(fmt, day)}
                      </Button>
                    );
                  })}
                </Stack>
              </Box>
              {forField("availability") && (
                <Typography
                  variant="meta"
                  role="alert"
                  sx={{ color: tokens.error }}
                >
                  {forField("availability")}
                </Typography>
              )}
            </Stack>
          )}
        </Box>

        <Box>
          <TextField
            select
            size="small"
            label={t("offerOverrideDialog.pictureHere")}
            value={ownImage ? "own" : "inherit"}
            onChange={(e) => setOwnImage(e.target.value === "own")}
            fullWidth
            helperText={
              ownImage
                ? t("offerOverrideDialog.ownImageHelp")
                : original.imageUrl
                  ? t("offerOverrideDialog.inheritedChainPicture")
                  : t("offerOverrideDialog.inheritedNoPicture")
            }
          >
            <MenuItem value="inherit">
              {t("offerOverrideDialog.inheritPicture")}
            </MenuItem>
            <MenuItem value="own">
              {t("offerOverrideDialog.ownPicture")}
            </MenuItem>
          </TextField>
          {ownImage && (
            <Box sx={{ mt: 1.5 }}>
              <AssetField
                label={t("common:labels.picture")}
                value={imageUrl}
                preview
                onChange={(v) => setImageUrl(v)}
                testId="override-image"
              />
              {forField("image") && (
                <Typography
                  variant="meta"
                  role="alert"
                  sx={{ mt: 0.75, color: tokens.error }}
                >
                  {forField("image")}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
      </Stack>
    </ItemFormSurface>
  );
}
